/**
 * Timezone-agnostic invoice date/time.
 *
 * Invoice dates are wall-clock values: the digits the user chose (2026-09-05 00:00)
 * are stored and shown as those same digits on every device. They are NOT converted
 * through the browser or server timezone.
 *
 * Storage convention: timestamptz with UTC offset whose clock digits ARE the wall
 * clock (`2026-09-05T00:00:00.000Z` means "5 Sep 2026, 12:00 AM", not an instant
 * in UTC). Never use `new Date(local).toISOString()` to send, and never use
 * `toLocaleString()` / local getters to display.
 */

const DATE_TIME_LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const STORED_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

const pad = (n) => String(n).padStart(2, '0');

export const parseWallClock = (value) => {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes(),
      second: value.getUTCSeconds(),
    };
  }

  const text = String(value).trim();
  const match = text.match(STORED_RE);
  if (!match) return null;

  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] || 0),
  };

  if (!isValidWallClock(parts)) return null;
  return parts;
};

const isValidWallClock = ({ year, month, day, hour, minute, second }) => {
  if ([year, month, day, hour, minute, second].some((n) => Number.isNaN(n))) return false;
  const utc = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return (
    utc.getUTCFullYear() === year
    && utc.getUTCMonth() === month - 1
    && utc.getUTCDate() === day
    && utc.getUTCHours() === hour
    && utc.getUTCMinutes() === minute
    && utc.getUTCSeconds() === second
  );
};

export const toStoredWallClock = (value) => {
  const parts = parseWallClock(value);
  if (!parts) return null;
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}.000Z`;
};

/** datetime-local value → stored wall clock. Digits are kept; no TZ conversion. */
export const wallClockFromDateTimeLocal = (value) => {
  if (!value || typeof value !== 'string') return null;
  const match = value.trim().match(DATE_TIME_LOCAL_RE);
  if (!match) return null;
  return toStoredWallClock(`${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6] || '00'}Z`);
};

/** Stored wall clock → datetime-local value. Digits are kept; no TZ conversion. */
export const dateTimeLocalFromWallClock = (value) => {
  const parts = parseWallClock(value);
  if (!parts) return '';
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
};

/** Current device clock digits, for picker max / first-invoice default. */
export const nowDateTimeLocal = (now = new Date()) => (
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
);

export const nowAsStoredWallClock = (now = new Date()) => wallClockFromDateTimeLocal(nowDateTimeLocal(now));

export const formatWallClockDate = (value) => {
  const parts = parseWallClock(value);
  if (!parts) return '';
  return `${pad(parts.month)}/${pad(parts.day)}/${parts.year}`;
};

export const formatWallClockDateTime = (value) => {
  const parts = parseWallClock(value);
  if (!parts) return '';
  const hour12 = parts.hour % 12 || 12;
  const ampm = parts.hour >= 12 ? 'PM' : 'AM';
  return `${formatWallClockDate(value)} ${hour12}:${pad(parts.minute)} ${ampm}`;
};

export const addDaysToWallClockDate = (value, days) => {
  const parts = parseWallClock(value);
  if (!parts) return null;
  const utc = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day + days,
    parts.hour,
    parts.minute,
    parts.second,
  ));
  return toStoredWallClock(utc);
};

export const wallClockCompareKey = (value) => {
  const parts = parseWallClock(value);
  if (!parts) return '';
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`;
};

/**
 * ±1 calendar-day grace for TZ drift across devices / DB / API.
 * Min = 00:00 on the day before job created; max = 23:59 on the UTC day after now
 * (same rule as API `invoiceDateMaxBound`).
 */
export const invoiceDateMinDateTimeLocal = (jobCreatedAt) => {
  const parts = parseWallClock(jobCreatedAt);
  if (!parts) return '';
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day - 1, 0, 0, 0));
  return `${utc.getUTCFullYear()}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())}T00:00`;
};

/** Same rule as API `invoiceDateMaxBound`: UTC calendar day after `now`, 23:59. */
export const invoiceDateMaxDateTimeLocal = (now = new Date()) => {
  const utc = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    23,
    59,
    0,
  ));
  return `${utc.getUTCFullYear()}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())}T23:59`;
};

export const getInvoiceDateValidationMessage = ({
  dateTimeLocal,
  jobCreatedAt,
  now = new Date(),
} = {}) => {
  if (!dateTimeLocal) return 'Enter a valid date and time';
  const stored = wallClockFromDateTimeLocal(dateTimeLocal);
  if (!stored) return 'Enter a valid date and time';

  const minKey = invoiceDateMinDateTimeLocal(jobCreatedAt);
  if (minKey && dateTimeLocal < minKey) {
    return 'Invoice date cannot be before the job was created';
  }

  const maxKey = invoiceDateMaxDateTimeLocal(now);
  if (dateTimeLocal > maxKey) {
    return 'Invoice date cannot be in the future';
  }

  return '';
};
